import { useMemo } from "react";
import Decimal from "decimal.js";
import { MarkdownReact } from "@/components/MarkdownReact";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatWeight } from "@/lib/utils";
import {
  formatQuoteAmount,
  formatQuoteLineTotal,
  formatQuoteUnitPrice,
  getQuoteDisplayTotals,
} from "@/lib/quoteCurrency";
import {
  WEIGHT_UNIT_ABBREVIATION,
  type PublicQuote,
  type PublicQuoteItemLine,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { formatDateOnly } from "@forgepoint/shared";
import {
  formatAddressLines,
  getQuoteEffectiveDate,
  getBreakWeightUnit,
  getTotalWeightsByUnit,
  getItemLineDescription,
  publicQuotePeopleNames,
} from "../utils";

interface PublicQuoteTemplateProfessionalGridProps {
  quote: PublicQuote;
  isExpired: boolean;
}

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

const formatDate = (value?: Date | string | null) => {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    return formatDateOnly(value, DATE_FORMAT_OPTIONS, "en-US");
  }

  if (Number.isNaN(value.getTime())) {
    return null;
  }

  return value.toLocaleDateString("en-US", DATE_FORMAT_OPTIONS);
};

/**
 * Formats an address as a single comma-separated string.
 * @param address - The address to format
 * @returns A formatted address string, or null if no address data
 */
const formatAddress = (address?: Parameters<typeof formatAddressLines>[0]) => {
  const lines = formatAddressLines(address);
  if (!lines) {
    return null;
  }
  return lines.join(", ");
};

const calculateEstimatedTax = (
  total: string | null,
  baseline: string | null,
) => {
  if (!total || !baseline) {
    return null;
  }

  const totalValue = new Decimal(total);
  const baseValue = new Decimal(baseline);

  const diff = totalValue.minus(baseValue);
  if (diff.lte(0)) {
    return null;
  }

  return diff.toString();
};

const formatContactLine = (phone?: string | null, email?: string | null) => {
  const parts = [phone, email].filter((value): value is string =>
    Boolean(value),
  );
  if (parts.length === 0) {
    return null;
  }
  return parts.join(" • ");
};

const isSectionLine = (
  line: PublicQuoteLine,
): line is Extract<PublicQuoteLine, { type: "SECTION" }> =>
  line.type === "SECTION";

const isNoteLine = (
  line: PublicQuoteLine,
): line is Extract<PublicQuoteLine, { type: "NOTE" }> => line.type === "NOTE";

const PartyCard = ({
  label,
  primary,
  peopleNames,
  phone,
  email,
  placeholder,
  location,
}: {
  label: string;
  primary: string | null;
  peopleNames?: string;
  phone?: string | null;
  email?: string | null;
  placeholder: string;
  location?: string | null;
}) => {
  const contactLine = formatContactLine(phone, email);
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="font-medium text-slate-900">{primary ?? "—"}</p>
      {peopleNames && <p className="text-slate-700">{peopleNames}</p>}
      {location && <p className="text-xs text-slate-500">{location}</p>}
      <p className="text-xs text-slate-500">{contactLine ?? placeholder}</p>
    </div>
  );
};

export function PublicQuoteTemplateProfessionalGrid({
  quote,
  isExpired,
}: PublicQuoteTemplateProfessionalGridProps) {
  const itemLines = (quote.lines ?? []).filter(
    (line): line is PublicQuoteItemLine => line.type === "ITEM",
  );
  const sectionLines = (quote.lines ?? []).filter(isSectionLine);
  const noteLines = (quote.lines ?? []).filter(isNoteLine);

  const totalWeightsByUnit = useMemo(
    () => getTotalWeightsByUnit(quote.lines ?? []),
    [quote.lines],
  );

  const customerCompany = quote.customerCompany ?? null;
  const organizationAddress = formatAddress(quote.organization?.address);
  const customerAddress = formatAddress(customerCompany?.address);
  const shipToAddress = formatAddress(quote.shippingAddress);

  const customerPhone = customerCompany?.phone ?? null;
  const customerEmail = customerCompany?.email ?? null;

  const quoteDetails = [
    {
      label: "Quote No.",
      value: `#${quote.quoteDisplayNumber}`,
    },
    { label: "Revision", value: `Rev ${quote.revision}` },
    { label: "Issued", value: formatDate(getQuoteEffectiveDate(quote)) },
    { label: "Valid Until", value: formatDate(quote.expirationDate) },
    { label: "Prepared By", value: quote.organization?.name },
  ];

  const chipDetails: { label: string; value: string }[] = [];
  if (quote.deliveryTerms) {
    chipDetails.push({ label: "Delivery", value: quote.deliveryTerms });
  }
  if (quote.paymentTerms) {
    chipDetails.push({ label: "Payment", value: quote.paymentTerms });
  }
  const validUntil = formatDate(quote.expirationDate);
  if (validUntil) {
    chipDetails.push({ label: "Valid", value: validUntil });
  }

  const estimatedTax = calculateEstimatedTax(
    quote.total,
    quote.subtotalWithDiscounts ?? quote.subtotal,
  );

  const displayTotals = getQuoteDisplayTotals(quote);

  return (
    <>
      {quote.enableCoverLetter && quote.coverLetterMarkdown && (
        <Card
          className="p-6 mb-6 shadow-sm border border-slate-200"
          style={{ breakAfter: "page", pageBreakAfter: "always" }}
        >
          <div className="prose prose-sm max-w-none text-slate-700">
            <MarkdownReact>{quote.coverLetterMarkdown}</MarkdownReact>
          </div>
        </Card>
      )}
      <Card className="bg-slate-50 p-0 text-slate-900 shadow-none border-none rounded-none sm:rounded-2xl">
        <div className="space-y-6">
          <div className="rounded-none sm:rounded-2xl bg-gradient-to-r from-blue-600 to-sky-500 text-white p-6 sm:p-8 shadow-lg">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                {quote.organization?.logoUrl ? (
                  <img
                    src={quote.organization.logoUrl}
                    alt={`${quote.organization.name} logo`}
                    className="h-14 w-14 rounded-xl bg-white/10 object-contain p-2"
                  />
                ) : (
                  <div className="h-14 w-14 rounded-xl bg-white/20 grid place-items-center text-lg font-bold">
                    {quote.organization?.name?.charAt(0) || "Q"}
                  </div>
                )}
                <div>
                  <p className="text-sm uppercase tracking-[0.3em] text-white/80">
                    {quote.organization?.name || "Your Company"}
                  </p>
                  <h1 className="text-2xl font-semibold tracking-wide">
                    {quote.name || "Quote"}
                  </h1>
                  {isExpired && (
                    <p className="mt-1 text-sm font-semibold text-red-100">
                      Expired — please request a refreshed version.
                    </p>
                  )}
                </div>
              </div>
              <div className="text-sm text-white/80 space-y-1 text-left sm:text-right">
                {quote.organization?.phone && (
                  <p>Phone • {quote.organization.phone}</p>
                )}
                {quote.organization?.website && (
                  <p>{quote.organization.website}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6 px-4 sm:px-8 pb-8">
            <div className="flex flex-wrap gap-2">
              {chipDetails.map((chip) => (
                <Badge
                  key={`${chip.label}-${chip.value}`}
                  variant="secondary"
                  className="rounded-full bg-slate-100 text-slate-700 border-none px-3 py-1 text-xs font-medium uppercase tracking-wide"
                >
                  {chip.label}: {chip.value}
                </Badge>
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
              <Card className="p-6 shadow-sm border border-slate-200">
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-slate-800">
                    Prepared For
                  </h2>
                  <div
                    className={`grid grid-cols-1 gap-4 ${shipToAddress ? "sm:grid-cols-3" : "sm:grid-cols-2"} text-sm`}
                  >
                    <PartyCard
                      label="Company"
                      primary={
                        customerCompany?.name ||
                        quote.name ||
                        `Quote #${quote.quoteDisplayNumber}`
                      }
                      peopleNames={publicQuotePeopleNames(quote)}
                      phone={customerPhone}
                      email={customerEmail}
                      placeholder="Add customer phone/email"
                      location={customerAddress}
                    />
                    {shipToAddress && (
                      <PartyCard
                        label="Ship To"
                        primary={quote.shippingAddress?.name ?? null}
                        placeholder=""
                        location={shipToAddress}
                      />
                    )}
                    <PartyCard
                      label="Contact"
                      primary={quote.organization?.name ?? null}
                      phone={quote.organization?.phone ?? null}
                      email={quote.organization?.website ?? null}
                      placeholder="Add contact phone/email"
                      location={organizationAddress}
                    />
                  </div>
                </div>
              </Card>

              <Card className="p-6 shadow-sm border border-slate-200">
                <div className="space-y-4">
                  <h2 className="text-base font-semibold text-slate-800">
                    Quote
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
                    {quoteDetails.map((detail) => (
                      <div key={detail.label} className="space-y-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {detail.label}
                        </p>
                        <p className="font-medium text-slate-900">
                          {detail.value ?? "—"}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            </div>

            <Card className="p-0 overflow-hidden shadow-sm border border-slate-200">
              <div className="p-4 sm:p-6">
                <h2 className="text-base font-semibold text-slate-800 mb-4">
                  Pricing
                </h2>

                {/* Mobile: Card-based layout */}
                <div className="space-y-3 md:hidden">
                  {itemLines.map((line, index) => {
                    const selectedQuantity = line.quantityBreaks?.[0]?.quantity;
                    const hasMultipleQuantityBreaks =
                      line.quantityBreaks && line.quantityBreaks.length > 1;
                    const lineTotal = line.amount ?? null;
                    const unitLabel = line.unit || "units";
                    const itemDescription = getItemLineDescription(line);
                    const singleWeightUnit = getBreakWeightUnit(
                      line.quantityBreaks?.[0],
                      line,
                    );

                    return (
                      <div
                        key={`mobile-${line.id}-${index}`}
                        className="rounded-lg border border-slate-100 bg-white p-4 shadow-sm break-inside-avoid"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-900 truncate">
                              {line.item?.name || line.description}
                            </p>
                            {itemDescription && (
                              <p className="whitespace-pre-line text-xs text-slate-500 mt-0.5 line-clamp-2">
                                {itemDescription}
                              </p>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-semibold text-slate-900">
                              {lineTotal
                                ? formatQuoteLineTotal(
                                    line.quantityBreaks?.[0],
                                    quote,
                                  )
                                : "—"}
                            </p>
                          </div>
                        </div>

                        {hasMultipleQuantityBreaks ? (
                          <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden">
                            <div className="bg-slate-100 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                              Quantity Pricing
                            </div>
                            <div className="divide-y divide-slate-100">
                              {line.quantityBreaks?.map((qb, idx) => {
                                const isSelected =
                                  selectedQuantity === qb.quantity;
                                const weightUnit = getBreakWeightUnit(qb, line);
                                return (
                                  <div
                                    key={`${qb.quantity}-${idx}`}
                                    className={`px-2 py-2 ${
                                      isSelected
                                        ? "bg-blue-50 font-semibold"
                                        : "bg-white"
                                    }`}
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs">
                                        {qb.quantity} {unitLabel}
                                      </span>
                                      <span className="text-sm font-semibold">
                                        {formatQuoteLineTotal(qb, quote)}
                                      </span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                      {formatQuoteUnitPrice(qb, quote)} each
                                    </div>
                                    {quote.showTotalWeight &&
                                      qb.totalWeight &&
                                      weightUnit && (
                                        <div className="text-xs text-slate-500 mt-0.5">
                                          Weight: {formatWeight(qb.totalWeight)}{" "}
                                          {WEIGHT_UNIT_ABBREVIATION[weightUnit]}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          line.quantityBreaks &&
                          line.quantityBreaks.length === 1 && (
                            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                              <span>
                                <span className="font-medium">Qty:</span>{" "}
                                {selectedQuantity ?? "—"}
                              </span>
                              {line.unit && (
                                <span>
                                  <span className="font-medium">UOM:</span>{" "}
                                  {line.unit}
                                </span>
                              )}
                              <span>
                                <span className="font-medium">Unit:</span>{" "}
                                {line.quantityBreaks[0]
                                  ? formatQuoteUnitPrice(
                                      line.quantityBreaks[0],
                                      quote,
                                    )
                                  : "—"}
                              </span>
                              {quote.showTotalWeight &&
                                line.quantityBreaks[0]?.totalWeight &&
                                singleWeightUnit && (
                                  <span>
                                    <span className="font-medium">Weight:</span>{" "}
                                    {formatWeight(
                                      line.quantityBreaks[0].totalWeight,
                                    )}{" "}
                                    {WEIGHT_UNIT_ABBREVIATION[singleWeightUnit]}
                                  </span>
                                )}
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
                  {itemLines.length === 0 && (
                    <div className="text-center text-sm text-muted-foreground py-6">
                      Add items to the quote to populate pricing.
                    </div>
                  )}
                </div>

                {/* Desktop: Table layout */}
                <div className="hidden md:block rounded-xl border border-slate-100 bg-white shadow-sm overflow-hidden">
                  <Table className="[&_th]:text-xs [&_th]:uppercase [&_th]:tracking-wide [&_th]:text-slate-500">
                    <TableHeader className="bg-slate-50/80">
                      <TableRow className="border-slate-100">
                        <TableHead className="w-[32%]">
                          Item / Service
                        </TableHead>
                        <TableHead>Specification</TableHead>
                        <TableHead className="w-[10%]">UOM</TableHead>
                        <TableHead className="text-right w-[10%]">
                          Qty
                        </TableHead>
                        <TableHead className="text-right w-[15%]">
                          Unit
                        </TableHead>
                        <TableHead className="text-right w-[15%]">
                          Line Total
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itemLines.map((line, index) => {
                        const selectedQuantity =
                          line.quantityBreaks?.[0]?.quantity;
                        const hasMultipleQuantityBreaks =
                          line.quantityBreaks && line.quantityBreaks.length > 1;
                        const lineTotal = line.amount ?? null;
                        const unitLabel = line.unit || "units";
                        const itemDescription = getItemLineDescription(line);

                        if (hasMultipleQuantityBreaks) {
                          return (
                            <TableRow
                              key={`${line.id}-${index}`}
                              className="border-slate-100 break-inside-avoid"
                            >
                              <TableCell
                                colSpan={6}
                                className="font-medium text-slate-900 p-4"
                              >
                                <div className="space-y-3">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="font-medium text-slate-900">
                                        {line.item?.name || line.description}
                                      </div>
                                      {itemDescription && (
                                        <div className="whitespace-pre-line text-sm text-slate-600 mt-1">
                                          {itemDescription}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right flex-shrink-0 ml-4">
                                      <div className="text-base font-semibold text-slate-900">
                                        {lineTotal
                                          ? formatQuoteLineTotal(
                                              line.quantityBreaks?.[0],
                                              quote,
                                            )
                                          : "—"}
                                      </div>
                                      <div className="text-xs text-slate-500 mt-0.5">
                                        Selected Total
                                      </div>
                                    </div>
                                  </div>

                                  <div className="rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
                                    <div className="px-3 py-2 bg-slate-100 border-b border-slate-200">
                                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                        Quantity Pricing Options
                                      </span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                      {line.quantityBreaks?.map((qb, idx) => {
                                        const isSelected =
                                          selectedQuantity === qb.quantity;
                                        const weightUnit = getBreakWeightUnit(
                                          qb,
                                          line,
                                        );
                                        return (
                                          <div
                                            key={`${qb.quantity}-${idx}`}
                                            className={`grid grid-cols-[1fr_auto_auto_auto] gap-4 px-3 py-2 text-sm ${
                                              isSelected
                                                ? "bg-blue-50 font-semibold"
                                                : "bg-white"
                                            }`}
                                          >
                                            <span className="text-slate-600">
                                              {unitLabel}
                                            </span>
                                            <span className="text-right min-w-[80px] tabular-nums">
                                              {qb.quantity}
                                            </span>
                                            <span className="text-right min-w-[100px] tabular-nums text-slate-600">
                                              {formatQuoteUnitPrice(qb, quote)}
                                            </span>
                                            <span className="text-right min-w-[100px] tabular-nums font-semibold">
                                              {formatQuoteLineTotal(qb, quote)}
                                            </span>
                                            {quote.showTotalWeight &&
                                              qb.totalWeight &&
                                              weightUnit && (
                                                <span className="col-span-4 text-xs text-slate-500 text-right">
                                                  Weight:{" "}
                                                  {formatWeight(qb.totalWeight)}{" "}
                                                  {
                                                    WEIGHT_UNIT_ABBREVIATION[
                                                      weightUnit
                                                    ]
                                                  }
                                                </span>
                                              )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        }

                        const singleBreak = line.quantityBreaks?.[0];
                        const singleWeightUnit = getBreakWeightUnit(
                          singleBreak,
                          line,
                        );
                        const singleQuantity = singleBreak?.quantity;
                        const singleUnitPrice =
                          singleBreak?.unitPriceOverride ??
                          singleBreak?.unitPrice;

                        return (
                          <TableRow
                            key={`${line.id}-${index}`}
                            className="border-slate-100 break-inside-avoid"
                          >
                            <TableCell className="font-medium text-slate-900">
                              {line.item?.name || line.description}
                            </TableCell>
                            <TableCell className="text-slate-600">
                              <span className="whitespace-pre-line">
                                {getItemLineDescription(line) || "—"}
                              </span>
                              {quote.showTotalWeight &&
                                line.quantityBreaks?.[0]?.totalWeight &&
                                singleWeightUnit && (
                                  <span className="block text-xs text-slate-500 mt-0.5">
                                    Weight:{" "}
                                    {formatWeight(
                                      line.quantityBreaks[0].totalWeight,
                                    )}{" "}
                                    {WEIGHT_UNIT_ABBREVIATION[singleWeightUnit]}
                                  </span>
                                )}
                            </TableCell>
                            <TableCell className="text-slate-600">
                              {line.unit || "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {singleQuantity ?? "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">
                              {singleUnitPrice
                                ? formatQuoteUnitPrice(singleBreak, quote)
                                : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm font-semibold text-slate-900">
                              {lineTotal
                                ? formatQuoteLineTotal(
                                    line.quantityBreaks?.[0],
                                    quote,
                                  )
                                : "—"}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {itemLines.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="text-center text-sm text-muted-foreground py-6"
                          >
                            Add items to the quote to populate pricing.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>

                {quote.showTotals && (
                  <div className="mt-6 grid gap-3 w-full sm:w-80 ml-auto break-inside-avoid">
                    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="font-semibold text-slate-900">
                        {quote.subtotal ? displayTotals.subtotalFormatted : "—"}
                      </span>
                    </div>
                    {estimatedTax && (
                      <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                        <span className="text-slate-500">Estimated Tax</span>
                        <span className="font-semibold text-slate-900">
                          {formatQuoteAmount(estimatedTax, quote)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between rounded-lg bg-blue-50 px-3 py-3 text-base font-semibold text-blue-900">
                      <span>Total</span>
                      <span>
                        {quote.total ? displayTotals.totalFormatted : "—"}
                      </span>
                    </div>
                    {quote.showTotalWeight &&
                      totalWeightsByUnit.length > 0 &&
                      totalWeightsByUnit.map(([unit, weight]) => (
                        <div
                          key={unit}
                          className="flex items-center justify-between px-3 py-2 text-sm text-gray-600"
                        >
                          <span>
                            Total Weight ({WEIGHT_UNIT_ABBREVIATION[unit]})
                          </span>
                          <span>{weight.toFixed(2)}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </Card>

            {quote.quoteFooter && (
              <div className="border-t border-slate-300 pt-4 mt-4">
                <p className="text-xs text-slate-500 font-bold whitespace-pre-wrap">
                  {quote.quoteFooter}
                </p>
              </div>
            )}

            <div className="grid gap-4 md:grid-cols-2">
              {quote.paymentTerms && (
                <Card className="p-6 shadow-sm border border-slate-200 break-inside-avoid">
                  <h3 className="text-base font-semibold text-slate-800 mb-3">
                    Payment Terms
                  </h3>
                  <div className="prose prose-sm text-slate-700 max-w-none">
                    <MarkdownReact>{quote.paymentTerms}</MarkdownReact>
                  </div>
                </Card>
              )}
              {quote.deliveryTerms && (
                <Card className="p-6 shadow-sm border border-slate-200 break-inside-avoid">
                  <h3 className="text-base font-semibold text-slate-800 mb-3">
                    Delivery Terms
                  </h3>
                  <div className="prose prose-sm text-slate-700 max-w-none">
                    <MarkdownReact>{quote.deliveryTerms}</MarkdownReact>
                  </div>
                </Card>
              )}
            </div>

            {quote.termsOfService && (
              <Card className="p-6 shadow-sm border border-slate-200 break-inside-avoid">
                <h3 className="text-base font-semibold text-slate-800 mb-3">
                  Other terms
                </h3>
                <div className="prose prose-sm text-slate-700 max-w-none">
                  <MarkdownReact>{quote.termsOfService}</MarkdownReact>
                </div>
                {sectionLines.length > 0 && (
                  <ul className="mt-4 list-disc space-y-1 pl-4 text-sm text-muted-foreground">
                    {sectionLines.map((line, index) => (
                      <li
                        key={`${line.description}-${index}`}
                        className="whitespace-pre-line"
                      >
                        {line.description}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            )}

            {noteLines.length > 0 && (
              <Card className="p-6 shadow-sm border border-amber-200 bg-amber-50 break-inside-avoid">
                <h3 className="text-base font-semibold text-slate-800 mb-3">
                  Notes
                </h3>
                <ul className="space-y-2">
                  {noteLines.map((line, index) => (
                    <li
                      key={`note-${index}`}
                      className="whitespace-pre-line text-sm text-amber-900 italic border-l-2 border-amber-300 pl-3"
                    >
                      {line.description}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {quote.isSigned && quote.signature && (
              <Card className="p-6 border border-slate-200 shadow-sm break-inside-avoid">
                <h3 className="text-base font-semibold text-slate-800 mb-4">
                  Acceptance
                </h3>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                      Authorized Signature
                    </p>
                    <div className="font-signature text-3xl text-slate-900 border-b border-slate-300 pb-2">
                      {quote.signature.name}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                      Date
                    </p>
                    <div className="text-sm text-slate-900 border-b border-slate-300 pb-2 pt-3">
                      {new Date(quote.signature.signedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <div className="text-xs text-slate-500 flex flex-wrap justify-between gap-2">
              <span>Confidential &amp; Proprietary.</span>
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}
